/**
 * @module NotificationsPage
 * @package @geenius/notifications-solidjs-css
 * @description Renders the full inbox page for the SolidJS vanilla CSS
 * variant.
 */

import { FadeIn } from '@geenius/motion/solidjs-css'
import type {
  Notification,
  NotificationStatus,
} from '@geenius/notifications/shared'
import {
  buildNotificationsSeoMeta,
  getNotificationStatusLabel,
  getNotificationTypeLabel,
  groupNotificationsForDisplay,
  NOTIFICATION_TYPES,
  TYPE_CONFIG,
} from '@geenius/notifications/shared'
import { SEOHead } from '@geenius/seo/solidjs-css'
import type { JSX } from 'solid-js'
import { createMemo, For, Show } from 'solid-js'
import { EmptyNotifications } from '../components/EmptyNotifications'
import { NotificationItem } from '../components/NotificationItem'
import { cx } from '../lib/cx'
import { createNotificationCopy } from '../lib/notificationCopy'
import { createNotifications } from '../primitives/createNotifications'

/**
 * Props for the SolidJS CSS notifications page.
 */
export interface NotificationsPageProps {
  notifications: Notification[] | undefined
  onMarkRead?: (id: string) => Promise<void>
  onMarkAllRead?: () => Promise<void>
  onDismiss?: (id: string) => Promise<void>
  onDelete?: (id: string) => Promise<void>
  class?: string
}

const NOTIFICATION_STATUS_FILTERS: ReadonlyArray<NotificationStatus | 'all'> = [
  'all',
  'unread',
  'read',
]

/**
 * Renders the full notifications inbox.
 *
 * @param props Page props including notification data and mutation handlers.
 * @returns The notifications page markup.
 */
export function NotificationsPage(props: NotificationsPageProps): JSX.Element {
  const { plural, t } = createNotificationCopy()
  const state = createNotifications(() => props.notifications, {
    markRead: props.onMarkRead ?? (async () => {}),
    markAllRead: props.onMarkAllRead ?? (async () => {}),
    dismiss: props.onDismiss ?? (async () => {}),
    deleteNotification: props.onDelete ?? (async () => {}),
  })

  const groupedSections = createMemo(() =>
    Array.from(groupNotificationsForDisplay(state.notifications(), t).entries()),
  )

  const seoMeta = buildNotificationsSeoMeta({
    title: t('notifications.seo.notificationsTitle'),
    description: t('notifications.seo.notificationsDescription'),
    path: '/notifications',
    keywords: ['notifications', 'inbox', 'alerts', 'activity'],
  })

  return (
    <>
      <SEOHead meta={seoMeta} />
      <Show
        when={!state.isLoading()}
        fallback={
          <div class={cx('notifications__skeleton-list', props.class)}>
            <For each={Array.from({ length: 6 })}>
              {() => (
                <div
                  class={cx(
                    'notifications__skeleton',
                    'notifications__skeleton--item',
                  )}
                />
              )}
            </For>
          </div>
        }
      >
        <div class={cx('notifications__page', props.class)}>
          <FadeIn class={cx('notifications__page-header')}>
            <div>
              <h1 class={cx('notifications__page-title')}>
                {t('notifications.common.title')}
              </h1>
              <p class={cx('notifications__page-subtitle')}>
                {plural('notifications.common.unreadSummary', state.unreadCount())}
              </p>
            </div>
            <Show when={state.unreadCount() > 0}>
              <button
                type="button"
                class={cx('notifications__btn', 'notifications__btn--primary')}
                onClick={() => props.onMarkAllRead?.()}
              >
                {t('notifications.common.markAllRead')}
              </button>
            </Show>
          </FadeIn>
          <FadeIn delay={60} class={cx('notifications__filter-tabs')}>
            <For each={NOTIFICATION_STATUS_FILTERS}>
              {(status) => (
                <button
                  type="button"
                  aria-pressed={state.statusFilter() === status}
                  class={cx(
                    'notifications__filter-tab',
                    state.statusFilter() === status
                      ? 'notifications__filter-tab--active'
                      : 'notifications__filter-tab--inactive',
                  )}
                  onClick={() => state.setStatusFilter(status)}
                >
                  {getNotificationStatusLabel(status, t)}
                </button>
              )}
            </For>
          </FadeIn>
          <FadeIn delay={120} class={cx('notifications__type-tabs')}>
            <button
              type="button"
              aria-pressed={state.typeFilter() === 'all'}
              class={cx(
                'notifications__type-tab',
                state.typeFilter() === 'all' && 'notifications__type-tab--active',
              )}
              onClick={() => state.setTypeFilter('all')}
            >
              {t('notifications.filters.typeAll')}
            </button>
            <For each={NOTIFICATION_TYPES}>
              {(type) => (
                <button
                  type="button"
                  aria-pressed={state.typeFilter() === type}
                  class={cx(
                    'notifications__type-tab',
                    `notifications__type-tab--${type}`,
                    state.typeFilter() === type && 'notifications__type-tab--active',
                  )}
                  onClick={() => state.setTypeFilter(type)}
                >
                  {TYPE_CONFIG[type].icon} {getNotificationTypeLabel(type, t)}
                </button>
              )}
            </For>
          </FadeIn>
          <Show when={state.notifications().length > 0} fallback={<EmptyNotifications />}>
            <For each={groupedSections()}>
              {([label, items], index) => (
                <FadeIn delay={160 + index() * 40}>
                  <div class={cx('notifications__date-group')}>
                    <h2 class={cx('notifications__date-label')}>{label}</h2>
                    <div class={cx('notifications__date-list')}>
                      <For each={items}>
                        {(notification) => (
                          <NotificationItem
                            notification={notification}
                            onRead={props.onMarkRead}
                            onDismiss={props.onDismiss}
                          />
                        )}
                      </For>
                    </div>
                  </div>
                </FadeIn>
              )}
            </For>
          </Show>
        </div>
      </Show>
    </>
  )
}
